/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { useState } from "react";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import useWebPubSubAlerts from "hooks/useWebPubSubAlerts";
import {
  getSystemNotificationState,
  requestSystemNotificationPermission,
  setSystemNotificationsEnabled,
} from "utils/systemNotifications";

const statusLabelMap = {
  idle: "Idle",
  connecting: "Connecting",
  connected: "Connected",
  reconnecting: "Reconnecting",
  disconnected: "Disconnected",
  error: "Error",
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "—";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const extractAlertText = (alert) => {
  const payload = alert?.raw?.payload ?? {};
  const rawTitle =
    payload?.title ??
    payload?.heading ??
    payload?.subject ??
    payload?.name ??
    alert?.title ??
    "Alert";
  const title =
    typeof rawTitle === "string"
      ? rawTitle.trim().length > 0
        ? rawTitle
        : "Alert"
      : String(rawTitle ?? "Alert");
  const detailCandidates = [
    payload?.message,
    payload?.body,
    payload?.description,
    payload?.detail,
    payload?.text,
  ]
    .map((text) => (typeof text === "string" ? text : null))
    .filter((text) => text && text.trim().length > 0);
  const detail =
    detailCandidates.find((text) => text.trim() !== title.trim()) ??
    (typeof alert?.title === "string" ? alert.title : null);

  return {
    title,
    detail: detail && detail.trim().length > 0 ? detail : null,
  };
};

const indicatorColorForGroup = (group = "") => {
  const normalized = String(group).toLowerCase();

  if (normalized.endsWith("_green")) return "#2e7d32";
  if (normalized.endsWith("_amber")) return "#f9a825";
  if (normalized.endsWith("_red")) return "#c62828";

  if (normalized.includes("green")) return "#2e7d32";
  if (normalized.includes("amber") || normalized.includes("orange")) return "#f9a825";
  if (normalized.includes("red")) return "#c62828";

  return "#546e7a";
};

function Notifications() {
  const [systemNotificationState, setSystemNotificationState] = useState(() =>
    getSystemNotificationState()
  );
  const {
    alerts,
    status: alertStatus,
    error: alertError,
    clearAllAlerts,
  } = useWebPubSubAlerts({ maxAlerts: 100 });

  const isConnecting = alertStatus === "connecting" || alertStatus === "reconnecting";
  const hasError = Boolean(alertError);
  const statusLabel = statusLabelMap[alertStatus] ?? alertStatus ?? "Idle";
  const hasAlerts = alerts.length > 0;

  const handleSystemNotifications = async () => {
    if (systemNotificationState === "enabled") {
      setSystemNotificationState(setSystemNotificationsEnabled(false));
      return;
    }
    if (systemNotificationState === "disabled") {
      setSystemNotificationState(setSystemNotificationsEnabled(true));
      return;
    }
    setSystemNotificationState(await requestSystemNotificationPermission());
  };

  const systemNotificationLabel =
    systemNotificationState === "enabled" ? "Disable pop-ups" : "Enable pop-ups";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" p={2} pb={1}>
                <MDBox>
                  <MDTypography variant="h5">Alerts</MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="caption" color="text">
                    Status: {statusLabel}
                  </MDTypography>
                  {hasAlerts ? (
                    <MDButton variant="outlined" color="info" size="small" onClick={clearAllAlerts}>
                      Clear
                    </MDButton>
                  ) : null}
                </MDBox>
              </MDBox>
              <MDBox px={2} pb={3}>
                <MDBox
                  display="flex"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={1}
                  mb={2}
                  p={1.5}
                  sx={{ backgroundColor: "#f7f7f8", borderRadius: "10px" }}
                >
                  <MDBox>
                    <MDTypography variant="button" fontWeight="medium">
                      System pop-up notifications
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      {systemNotificationState === "enabled"
                        ? "Enabled on this device."
                        : systemNotificationState === "denied"
                        ? "Blocked by this device. Allow notifications in the browser or app settings."
                        : systemNotificationState === "unsupported"
                        ? "Not supported here. On iPhone or iPad, install OmnioFarm to the Home Screen first."
                        : "Show new alerts in Android, iOS, or Windows notification pop-ups."}
                    </MDTypography>
                  </MDBox>
                  {systemNotificationState !== "unsupported" &&
                  systemNotificationState !== "denied" ? (
                    <MDButton
                      variant={systemNotificationState === "enabled" ? "outlined" : "contained"}
                      color="info"
                      size="small"
                      onClick={handleSystemNotifications}
                    >
                      {systemNotificationLabel}
                    </MDButton>
                  ) : null}
                </MDBox>
                {isConnecting ? (
                  <MDBox display="flex" alignItems="center" gap={1} py={2} color="text.secondary">
                    <CircularProgress size={18} thickness={5} />
                    <MDTypography variant="caption" color="text">
                      Connecting to alert stream...
                    </MDTypography>
                  </MDBox>
                ) : null}
                {hasError ? (
                  <MDBox py={isConnecting ? 0 : 2}>
                    <MDTypography variant="button" color="error">
                      Unable to load alerts.
                    </MDTypography>
                    {alertError?.message ? (
                      <MDTypography variant="caption" color="error">
                        {alertError.message}
                      </MDTypography>
                    ) : null}
                  </MDBox>
                ) : null}
                {hasAlerts ? (
                  <MDBox sx={{ overflowX: "auto" }}>
                    <MDBox display="flex" flexDirection="column" gap={1.5}>
                      {alerts.map((alert) => {
                        const { title, detail } = extractAlertText(alert);
                        const indicatorColor = indicatorColorForGroup(alert?.group);
                        return (
                          <MDBox
                            key={alert.id}
                            display="flex"
                            flexDirection="column"
                            gap={0.75}
                            sx={{
                              backgroundColor: "#f7f7f8",
                              borderRadius: "12px",
                              padding: "14px 16px",
                            }}
                          >
                            <MDTypography variant="caption" color="text" fontWeight="medium">
                              {formatTimestamp(alert.receivedAt)}
                            </MDTypography>
                            <MDBox display="flex" alignItems="flex-start" gap={1.5}>
                              <MDBox
                                component="span"
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  backgroundColor: indicatorColor,
                                  flexShrink: 0,
                                  mt: 0.4,
                                }}
                              />
                              <MDBox>
                                <MDTypography variant="button" fontWeight="medium" color="text">
                                  {title}
                                </MDTypography>
                                {detail && detail !== title ? (
                                  <MDTypography
                                    variant="caption"
                                    color="text"
                                    display="block"
                                    mt={0.5}
                                  >
                                    {detail}
                                  </MDTypography>
                                ) : null}
                              </MDBox>
                            </MDBox>
                          </MDBox>
                        );
                      })}
                    </MDBox>
                  </MDBox>
                ) : null}
                {!hasAlerts && !isConnecting && !hasError ? (
                  <MDBox py={3}>
                    <MDTypography variant="button" color="text" align="center" display="block">
                      No alerts yet.
                    </MDTypography>
                  </MDBox>
                ) : null}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Notifications;
